import { useEffect, useState } from 'react';

/**
 * Appearance: color mode (Light / Dark / System) and accent color.
 *
 * Owner direction, 2026-10-18: Light is the default presentation,
 * Dark keeps the redesign scheme, System follows the OS, and the
 * accent is selectable. The choice persists in localStorage on the
 * machine only (appearance is not project data and never leaves
 * the browser). public/theme-init.js applies the same keys before
 * first paint; this module owns the state once React is running.
 * The resolved scheme and the accent land on <html> as data-theme
 * and data-accent; styles.css keys every color off those two
 * attributes.
 */

export type ThemeMode = 'light' | 'dark' | 'system';
export type Scheme = 'light' | 'dark';
export type AccentId =
  | 'orange'
  | 'blue'
  | 'sky'
  | 'violet'
  | 'rose'
  | 'amber'
  | 'green'
  | 'slate'
  | 'mono';

export interface AccentDef {
  readonly id: AccentId;
  readonly label: string;
  /** The accent's field color; used for the swatch fill. */
  readonly color: string;
  /**
   * The plain color word for copy that names the accent (Step 3's
   * chart caption: "{captionWord} dots are the model estimates").
   * Owner direction, 2026-10-18: the caption names the ACTIVE
   * accent's color, so it stays true under every theme; under the
   * default Signal Orange it renders the original wording verbatim.
   */
  readonly captionWord: string;
}

export const ACCENTS: readonly AccentDef[] = [
  { id: 'orange', label: 'Signal Orange', color: '#ff8000', captionWord: 'orange' },
  { id: 'blue', label: 'Blue', color: '#2563eb', captionWord: 'blue' },
  { id: 'sky', label: 'Sky', color: '#0369a1', captionWord: 'sky blue' },
  { id: 'violet', label: 'Violet', color: '#7c3aed', captionWord: 'violet' },
  { id: 'rose', label: 'Rose', color: '#e11d48', captionWord: 'rose' },
  { id: 'amber', label: 'Amber', color: '#f59e0b', captionWord: 'amber' },
  { id: 'green', label: 'Green', color: '#15803d', captionWord: 'green' },
  { id: 'slate', label: 'Slate', color: '#64748b', captionWord: 'slate' },
  { id: 'mono', label: 'Mono', color: '#0b0b0c', captionWord: 'black' },
];

export const DEFAULT_MODE: ThemeMode = 'light';
export const DEFAULT_ACCENT: AccentId = 'orange';

/* Key names are shared verbatim with public/theme-init.js. */
export const MODE_STORAGE_KEY = 'sae-studio.appearance.mode';
export const ACCENT_STORAGE_KEY = 'sae-studio.appearance.accent';

const SCHEME_BG: Record<Scheme, string> = {
  light: '#f6f6f7',
  dark: '#0b0b0c',
};

function isMode(v: string | null): v is ThemeMode {
  return v === 'light' || v === 'dark' || v === 'system';
}

function isAccent(v: string | null): v is AccentId {
  return ACCENTS.some((a) => a.id === v);
}

function readStored(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStored(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* Storage unavailable (private mode, embedded webview): the
       choice still applies for the session. */
  }
}

export function loadMode(): ThemeMode {
  const v = readStored(MODE_STORAGE_KEY);
  return isMode(v) ? v : DEFAULT_MODE;
}

export function loadAccent(): AccentId {
  const v = readStored(ACCENT_STORAGE_KEY);
  return isAccent(v) ? v : DEFAULT_ACCENT;
}

export function resolveScheme(mode: ThemeMode): Scheme {
  if (mode !== 'system') return mode;
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  } catch {
    return 'light';
  }
}

/** Apply mode + accent to the document root (idempotent). */
export function applyAppearance(mode: ThemeMode, accent: AccentId): Scheme {
  const scheme = resolveScheme(mode);
  const root = document.documentElement;
  root.dataset.theme = scheme;
  root.dataset.accent = accent;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta !== null) meta.setAttribute('content', SCHEME_BG[scheme]);
  return scheme;
}

export interface Appearance {
  readonly mode: ThemeMode;
  readonly accent: AccentId;
  readonly setMode: (mode: ThemeMode) => void;
  readonly setAccent: (accent: AccentId) => void;
}

export function useAppearance(): Appearance {
  const [mode, setModeState] = useState<ThemeMode>(loadMode);
  const [accent, setAccentState] = useState<AccentId>(loadAccent);

  useEffect(() => {
    applyAppearance(mode, accent);
  }, [mode, accent]);

  /* System mode tracks the OS live: a scheme flip re-resolves and
     re-applies without touching the stored preference. */
  useEffect(() => {
    if (mode !== 'system') return;
    let mql: MediaQueryList;
    try {
      mql = window.matchMedia('(prefers-color-scheme: dark)');
    } catch {
      return;
    }
    const onChange = () => applyAppearance('system', accent);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [mode, accent]);

  return {
    mode,
    accent,
    setMode: (next) => {
      writeStored(MODE_STORAGE_KEY, next);
      setModeState(next);
    },
    setAccent: (next) => {
      writeStored(ACCENT_STORAGE_KEY, next);
      setAccentState(next);
    },
  };
}
